import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowDownLeft, FiSend, FiExternalLink, FiClock, FiInfo } from 'react-icons/fi';
import { RiSwapLine, RiHistoryLine } from 'react-icons/ri';
import { useWallet } from '../context/WalletContext';
import TonWeb from 'tonweb';
import Header from './Header';
import TokenIcon from './TokenIcon';
import '../styles/TokenPage.css';
import AppLoading from './AppLoading';

// Import MAIN_ASSETS configuration
const MAIN_ASSETS = {
  /*
  TGR: {
    mainnetAddress: 'EQBvW8Z5huBkMJYdnfAEM5JqTNkuWX3diqYENkWWgRZHRq5s',
    testnetAddress: 'EQC_1YoM8RBixN95lz7odcF3Vrkc_N8Ne9yXV5gS0UQPntr_',
    name: 'Trigo',
    symbol: 'TGR',
    decimals: 9,
    iconClass: 'trigo',
    icon: '₮',
    category: 'Platform Token',
    chain: 'TON',
    image: '/assets/tokens/tgr.png',
    fallbackImage: 'https://ui-avatars.com/api/?name=TGR&background=34D399&color=fff&size=128'
  },
  */
  XAU: {
    mainnetAddress: '0x0000000000000000000000000000000000000000',
    testnetAddress: '0x0000000000000000000000000000000000000000',
    name: 'EGold',
    symbol: 'XAU',
    decimals: 18,
    iconClass: 'xau',
    icon: 'G',
    category: 'Platform Token',
    chain: 'EGC',
    image: require('../assests/EGoldChain Logo Symbol.png'),
    fallbackImage: 'https://ui-avatars.com/api/?name=XAU&background=D4AF37&color=fff&size=128'
  },
  TON: {
    name: 'Toncoin',
    symbol: 'TON',
    decimals: 9,
    iconClass: 'ton',
    icon: '◇',
    category: 'Network Token',
    chain: 'TON',
    coingeckoId: 'the-open-network',
    image: '/assets/tokens/ton.png',
    fallbackImage: 'https://ui-avatars.com/api/?name=TON&background=2B5AFF&color=fff&size=128'
  },
  BNB: {
    name: 'BNB',
    symbol: 'BNB',
    decimals: 18,
    iconClass: 'bnb',
    icon: 'BNB',
    category: 'Network Token',
    chain: 'BNB',
    coingeckoId: 'binancecoin',
    image: '/assets/tokens/bnb.png',
    fallbackImage: 'https://ui-avatars.com/api/?name=BNB&background=F3BA2F&color=fff&size=128'
  },
  USDT: {
    mainnetAddress: 'EQBynBO23ywHy_CgarY9NK9FTz0yDsG82PtcYl_vy6UEZqoP',
    testnetAddress: 'EQB-MPwrd1G6WKNkLz_VnV6WqBDd142KMQv-g1O-8QUA3728',
    name: 'Tether',
    symbol: 'USDT',
    decimals: 6,
    iconClass: 'usdt',
    icon: '$',
    category: 'Stablecoin',
    chain: 'TON',
    coingeckoId: 'tether',
    image: '/assets/tokens/usdt.png',
    fallbackImage: 'https://ui-avatars.com/api/?name=USDT&background=26A17B&color=fff&size=128'
  },
  USDC: {
    mainnetAddress: 'EQB-6RaBJT5bBPmgZUlBEgTvN3R0Gb_BBgPE8kqZWVwPUYKF',
    testnetAddress: 'EQBCFwW8uFUh-amdRmNY9NyeDEaeDYXd_xtFsnw5bbxPQh7q',
    name: 'USD Coin',
    symbol: 'USDC',
    decimals: 6,
    iconClass: 'usdc',
    icon: '$',
    category: 'Stablecoin',
    chain: 'TON',
    coingeckoId: 'usd-coin',
    image: '/assets/tokens/usdc.png',
    fallbackImage: 'https://ui-avatars.com/api/?name=USDC&background=2775CA&color=fff&size=128'
  }
};

// Helper functions for localStorage caching (per token symbol)
function loadCachedTokenPrice(symbol) {
  try {
    const raw = localStorage.getItem(`tokenPrice_${symbol}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
function saveCachedTokenPrice(symbol, priceData) {
  try {
    localStorage.setItem(`tokenPrice_${symbol}`, JSON.stringify(priceData));
  } catch { }
}
function loadCachedTokenBalance(symbol) {
  try {
    const raw = localStorage.getItem(`tokenBalance_${symbol}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Check for expiry (90 seconds)
    if (parsed && parsed.timestamp && Date.now() - parsed.timestamp < 90000) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}
function saveCachedTokenBalance(symbol, balance) {
  try {
    localStorage.setItem(`tokenBalance_${symbol}`, JSON.stringify({ balance, timestamp: Date.now() }));
  } catch { }
}

function loadCachedMainAssetPrices() {
  try {
    const raw = localStorage.getItem('mainAssetPrices');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

const TokenPage = () => {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const { wallet, balance, tokenBalances, loading, getTokenPrice, showToast, transactions: globalTransactions, chainBalances } = useWallet();
  const [activeTab, setActiveTab] = useState('activities');
  const [error, setError] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [priceData, setPriceData] = useState(() => loadCachedTokenPrice(symbol) || { price: 0, change24h: 0, lastUpdated: null });
  const [tokenData, setTokenData] = useState(() => {
    const cached = loadCachedTokenBalance(symbol);
    if (cached) {
      // Use cached as a fallback, but will be replaced by live data
      return { symbol, balance: cached.balance };
    }
    return null;
  });
  const [loadingPrice, setLoadingPrice] = useState(true);
  const [transactionFilter, setTransactionFilter] = useState('all');
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const mainAssetPrices = loadCachedMainAssetPrices() || {};

  useEffect(() => {
    if (!wallet && !loading) {
      navigate('/welcome');
      return;
    }

    const loadTokenData = async (forceRefresh = false) => {
      if (isRefreshing) return;

      try {
        setError(null);
        console.log('Loading token data for symbol:', symbol);

        let newTokenData;
        if (symbol === 'TON') {
          newTokenData = {
            ...MAIN_ASSETS.TON,
            balance: balance || '0'
          };
        } else if (symbol === 'XAU') {
          newTokenData = {
            ...MAIN_ASSETS.XAU,
            balance: tokenBalances['XAU']?.balance || '0'
          };
          // } else if (symbol === 'TRG') {
          //   newTokenData = {
          //     ...MAIN_ASSETS.TGR,
          //     balance: tokenBalances['TGR']?.balance || '0'
          //   };
        } else if (symbol === 'USDT') {
          newTokenData = {
            ...MAIN_ASSETS.USDT,
            balance: tokenBalances['USDT']?.balance || '0'
          };
        } else if (symbol === 'USDC') {
          newTokenData = {
            ...MAIN_ASSETS.USDC,
            balance: tokenBalances['USDC']?.balance || '0'
          };
        } else if (symbol === 'BNB') {
          newTokenData = {
            ...MAIN_ASSETS.BNB,
            balance: chainBalances?.BNB || '0'
          };
        } else if (symbol in tokenBalances) {
          // Handle other chain tokens
          const tokenDataRaw = tokenBalances[symbol]?.data;
          if (!tokenDataRaw) {
            throw new Error('Token data not found');
          }
          newTokenData = {
            name: tokenDataRaw.name,
            symbol: tokenDataRaw.symbol,
            decimals: tokenDataRaw.decimals,
            iconClass: tokenDataRaw.symbol.toLowerCase(),
            icon: tokenDataRaw.symbol,
            category: 'Chain Token',
            image: `/assets/tokens/${tokenDataRaw.symbol.toLowerCase()}.png`,
            fallbackImage: `https://ui-avatars.com/api/?name=${tokenDataRaw.symbol}&background=F3BA2F&color=fff&size=128`,
            balance: tokenBalances[symbol]?.balance || '0'
          };
        } else {
          throw new Error('Invalid token symbol');
        }
        setTokenData(newTokenData);
        saveCachedTokenBalance(symbol, newTokenData.balance);

        // Fetch token price
        let price;

        // Use mainAssetPrices for main assets
        if (["TON", "BNB", "USDT", "USDC", "XAU"].includes(symbol) && mainAssetPrices[symbol]) {
          price = mainAssetPrices[symbol];
        } else if (symbol === 'BNB' || tokenBalances[symbol]?.chain === 'BNB') {
          try {
            const coingeckoId = symbol === 'BNB' ? 'binancecoin' : tokenBalances[symbol]?.coingeckoId;
            const response = await fetch(
              `https://api.coingecko.com/api/v3/simple/price?ids=${coingeckoId || 'binancecoin'}&vs_currencies=usd&include_24h_change=true`
            );
            const data = await response.json();
            const tokenId = coingeckoId || 'binancecoin';
            price = {
              price: data[tokenId]?.usd || 0,
              change24h: data[tokenId]?.usd_24h_change || 0
            };
          } catch (error) {
            console.error('Error fetching BNB chain price:', error);
            price = { price: 0, change24h: 0 };
          }
        } else {
          // Use TON chain price API for TON tokens
          price = await getTokenPrice(symbol === 'TON' ? null : MAIN_ASSETS[symbol]?.testnetAddress);
        }

        setPriceData({
          price: price.price || 0,
          change24h: price.change24h || 0,
          lastUpdated: Date.now()
        });
        saveCachedTokenPrice(symbol, {
          price: price.price || 0,
          change24h: price.change24h || 0,
          lastUpdated: Date.now()
        });
        setLoadingPrice(false);

        // Show toast only if data has not been loaded before and not refreshing
        if (!hasLoadedData && !isRefreshing) {
          showToast('success', 'Token data loaded successfully!');
          setHasLoadedData(true);
        }
      } catch (err) {
        // On error, use cached price and balance if available
        const cachedPrice = loadCachedTokenPrice(symbol);
        if (cachedPrice) setPriceData(cachedPrice);
        const cachedBalance = loadCachedTokenBalance(symbol);
        if (cachedBalance && (!tokenData || !tokenData.balance)) setTokenData(prev => ({ ...prev, balance: cachedBalance.balance }));
        console.error('Error loading token data:', err);
        if (!isRefreshing) {
          showToast('error', `Failed to load ${symbol} token data`, { toastId: 'token-load-error' });
        }
        setError(err.message);
      } finally {
        setIsRefreshing(false);
      }
    };

    // Check cache expiry
    const cached = loadCachedTokenBalance(symbol);
    if (symbol && (!hasLoadedData || isRefreshing || !cached)) {
      loadTokenData();
    }
  }, [symbol, hasLoadedData, isRefreshing, wallet, loading, balance, tokenBalances, getTokenPrice, showToast, navigate, mainAssetPrices, chainBalances]);

  // Function to force refresh after send/receive
  const forceRefreshBalance = useCallback(() => {
    setIsRefreshing(true);
    setHasLoadedData(false);
  }, []);

  // Filter global transactions for this token
  const filteredTransactions = useMemo(() => {
    if (!globalTransactions || !symbol) return [];
    if (symbol === 'TON') {
      return globalTransactions.filter(tx => tx.symbol === 'TON');
    }
    return globalTransactions.filter(tx => tx.symbol === symbol);
  }, [globalTransactions, symbol]);

  // Fetch token transactions
  useEffect(() => {
    const fetchTransactions = async () => {
      if (!wallet?.address || !tokenData) return;

      try {
        setLoadingTransactions(true);
        const tonweb = new TonWeb(new TonWeb.HttpProvider(
          'https://toncenter.com/api/v2/jsonRPC'
        ));
        const txs = await tonweb.getTransactions(wallet.address, 50);

        // Filter and format transactions
        const formattedTxs = txs
          .map(tx => {
            try {
              const isOutgoing = tx.in_msg?.source === wallet.address;
              const amount = tx.in_msg?.value || '0';

              return {
                id: tx.hash,
                type: isOutgoing ? 'send' : 'receive',
                amount: amount,
                from: tx.in_msg?.source || 'Unknown',
                to: tx.in_msg?.destination || 'Unknown',
                timestamp: tx.utime,
                fee: tx.fee,
                symbol: tokenData.symbol
              };
            } catch (err) {
              console.warn('Error processing transaction:', err);
              return null;
            }
          })
          .filter(tx => tx && (transactionFilter === 'all' || tx.type === transactionFilter));

        setTransactions(formattedTxs);
      } catch (error) {
        console.error('Error fetching transactions:', error);
      } finally {
        setLoadingTransactions(false);
      }
    };

    fetchTransactions();
  }, [wallet?.address, tokenData, transactionFilter]);

  const formatBalance = (balance) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(balance);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const formatAddress = (address) => {
    if (!address || address === 'Unknown') return 'Unknown';
    try {
      const addr = new TonWeb.Address(address);
      const formatted = addr.toString({ urlSafe: true, bounceable: true });
      return `${formatted.slice(0, 6)}...${formatted.slice(-4)}`;
    } catch (err) {
      return address.slice(0, 6) + '...' + address.slice(-4);
    }
  };

  if (loading) {
    return (
      <div className="token-page">
        <Header title="Loading..." />
        <div className="token-content">
          <motion.div
            className="token-header loading"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="token-info">
              <div className="token-icon loading-skeleton" style={{ width: '60px', height: '60px', borderRadius: '16px' }} />
              <div className="token-details">
                <div className="token-title">
                  <div className="loading-skeleton" style={{ width: '120px', height: '24px', marginBottom: '8px' }} />
                  <div className="loading-skeleton" style={{ width: '80px', height: '18px' }} />
                </div>
                <div className="token-price">
                  <div className="loading-skeleton" style={{ width: '100px', height: '20px' }} />
                </div>
              </div>
            </div>
            <div className="balance-section">
              <span className="label">Your Balance</span>
              <div className="loading-skeleton" style={{ width: '100%', height: '36px', marginBottom: '8px' }} />
              <div className="loading-skeleton" style={{ width: '60%', height: '18px' }} />
            </div>
          </motion.div>

          <div className="action-buttons" style={{ opacity: 0.7 }}>
            <motion.button className="action-button receive" disabled>
              <FiArrowDownLeft />
              <span>Receive</span>
            </motion.button>
            <motion.button className="action-button send" disabled>
              <FiSend />
              <span>Send</span>
            </motion.button>
            <motion.button className="action-button swap" disabled>
              <RiSwapLine />
              <span>Swap</span>
            </motion.button>
          </div>

          <div className="token-tabs">
            <button className="tab active">Activities</button>
            <button className="tab">About</button>
          </div>

          <div className="activities-section">
            <div className="activity-filters">
              <button className="filter active">All</button>
              <button className="filter">Receive</button>
              <button className="filter">Send</button>
            </div>

            <div className="transactions-list">
              {[1, 2, 3].map((i) => (
                <div key={i} className="transaction-item">
                  <div className="transaction-icon loading-skeleton" style={{ width: '42px', height: '42px', borderRadius: '14px' }} />
                  <div className="transaction-details">
                    <div className="transaction-main">
                      <div className="loading-skeleton" style={{ width: '60px', height: '18px' }} />
                      <div className="loading-skeleton" style={{ width: '80px', height: '18px' }} />
                    </div>
                    <div className="transaction-sub">
                      <div className="loading-skeleton" style={{ width: '90px', height: '14px' }} />
                      <div className="loading-skeleton" style={{ width: '70px', height: '14px' }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="token-page">
        <Header title="Error" />
        <motion.div
          className="error-message"
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <FiInfo size={24} style={{ marginBottom: '12px' }} />
          <span>{error}</span>
          <motion.button
            className="action-button"
            style={{ maxWidth: '200px', marginTop: '16px' }}
            onClick={() => navigate('/wallet')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Return to Wallet
          </motion.button>
        </motion.div>
      </div>
    );
  }

  if (!tokenData) {
    return (
      <div className="token-page">
        <Header title="Token Not Found" />
        <motion.div
          className="error-message"
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <FiInfo size={24} style={{ marginBottom: '12px' }} />
          <span>Token information not available</span>
          <motion.button
            className="action-button"
            style={{ maxWidth: '200px', marginTop: '16px' }}
            onClick={() => navigate('/wallet')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Return to Wallet
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="token-page">
      <Header
        title={`${tokenData?.name || 'Token'} (${symbol})`}
        onRefresh={forceRefreshBalance}
        isRefreshing={isRefreshing}
      />

      <div className="token-content">
        <motion.div
          className="token-header"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="token-header-center">
            <div className="token-logo-xlarge" style={{ margin: '0 auto 18px auto' }}>
              <TokenIcon token={tokenData} className="main" />
            </div>
            <motion.div
              className="token-title"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.3 }}
              style={{ textAlign: 'center', width: '100%' }}
            >
              <h1>{tokenData?.name}</h1>
              <div className="token-badges">
                {tokenData?.category && (
                  <span className="token-category">{tokenData.category}</span>
                )}
                {tokenData?.chain && tokenData?.chain !== tokenData?.category && (
                  <span className={`chain-badge ${tokenData.chain?.toLowerCase()}`}>
                    {tokenData.chain}
                  </span>
                )}
              </div>
            </motion.div>
            <motion.div
              className="token-price"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.3 }}
            >
              {loadingPrice ? (
                <motion.span
                  className="loading-price"
                  animate={{ opacity: [0.5, 0.8, 0.5] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  Loading price...
                </motion.span>
              ) : (
                <>
                  <span className="price">${priceData.price.toFixed(2)}</span>
                  <span className={`price-change ${priceData.change24h >= 0 ? 'positive' : 'negative'}`}
                    style={{ marginLeft: 8 }}>
                    {priceData.change24h >= 0 ? '+' : ''}{priceData.change24h.toFixed(2)}% {priceData.change24h >= 0 ? '▲' : '▼'}
                  </span>
                </>
              )}
            </motion.div>
            <motion.div
              className="balance-section full-width"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.3 }}
            >
              {/* <span className="label">Your Balance</span> */}
              <div className="balance">
                <motion.span
                  className="amount"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.3 }}
                >
                  {formatBalance(tokenData?.balance || '0')} {symbol}
                </motion.span>
                <motion.span
                  className="value"
                  style={{ fontWeight: 700 }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5, duration: 0.3 }}
                >
                  ≈ ${((parseFloat(tokenData?.balance || '0') * priceData.price) || 0).toFixed(2)}
                </motion.span>
              </div>
            </motion.div>
          </div>
        </motion.div>

        <div className="action-buttons">
          <motion.button
            className="action-button receive"
            onClick={() => navigate('/receive')}
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.2 }}
          >
            <FiArrowDownLeft />
            <span>Receive</span>
          </motion.button>
          <motion.button
            className="action-button send"
            onClick={() => navigate('/send')}
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.2 }}
          >
            <FiSend />
            <span>Send</span>
          </motion.button>
          <motion.button
            className="action-button swap"
            onClick={() => navigate('/swap')}
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.2 }}
          >
            <RiSwapLine />
            <span>Swap</span>
          </motion.button>
        </div>

        <motion.div
          className="token-tabs"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.3 }}
        >
          <motion.button
            className={`tab ${activeTab === 'activities' ? 'active' : ''}`}
            onClick={() => setActiveTab('activities')}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <RiHistoryLine />
            Activities
          </motion.button>
          <motion.button
            className={`tab ${activeTab === 'about' ? 'active' : ''}`}
            onClick={() => setActiveTab('about')}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <FiInfo />
            About
          </motion.button>
        </motion.div>

        <AnimatePresence mode="wait">
          {activeTab === 'activities' ? (
            <motion.div
              className="activities-section"
              key="activities"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="activity-filters"
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.2 }}
              >
                {['all', 'receive', 'send'].map((filter) => (
                  <motion.button
                    key={filter}
                    className={`filter ${transactionFilter === filter ? 'active' : ''}`}
                    onClick={() => setTransactionFilter(filter)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {filter.charAt(0).toUpperCase() + filter.slice(1)}
                  </motion.button>
                ))}
              </motion.div>
              {/* Use HomePage-style transaction list */}
              {loadingTransactions ? (
                <TransactionsSkeleton />
              ) : filteredTransactions.length === 0 ? (
                <EmptyTransactions symbol={symbol} />
              ) : (
                <div className="transactions-list">
                  {filteredTransactions.map((tx, index) => (
                    <motion.div
                      key={tx.hash || tx.id || index}
                      className={`transaction-item ${tx.type}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05, duration: 0.2 }}
                      whileHover={{ x: 3 }}
                    >
                      <div className={`transaction-icon ${tx.type}`}>{tx.type === 'send' ? <FiSend /> : <FiArrowDownLeft />}</div>
                      <div className="transaction-details">
                        <div className="transaction-main">
                          <span className="type">{tx.type === 'send' ? 'Sent' : tx.type === 'receive' ? 'Received' : 'Token'}</span>
                          <span className={`amount ${tx.type}`}>{tx.formattedAmount} {tx.symbol}</span>
                        </div>
                        <div className="transaction-sub">
                          <span><FiClock size={12} /> {tx.formattedDate} {tx.formattedTime}</span>
                          <span>{tx.type === 'send' ? `To: ${tx.to}` : `From: ${tx.from}`}</span>
                        </div>
                      </div>
                      <a href={tx.explorerUrl} target="_blank" rel="noopener noreferrer" className="view-button"><FiExternalLink /></a>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              className="about-section"
              key="about"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <TokenInformation tokenData={tokenData} />
              <PriceStatistics priceData={priceData} />

              {tokenData?.testnetAddress && (
                <ExplorerButton address={tokenData.testnetAddress} />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// Helper Components
const TransactionsSkeleton = () => (
  <div className="transactions-list">
    {[1, 2, 3].map((i) => (
      <motion.div
        key={i}
        className="transaction-item loading"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 0.7, y: 0 }}
        transition={{ delay: i * 0.1, duration: 0.2 }}
      >
        <div className="transaction-icon loading-skeleton" />
        <div className="transaction-details">
          <div className="transaction-main">
            <div className="loading-skeleton" style={{ width: '60px', height: '18px' }} />
            <div className="loading-skeleton" style={{ width: '80px', height: '18px' }} />
          </div>
          <div className="transaction-sub">
            <div className="loading-skeleton" style={{ width: '90px', height: '14px' }} />
            <div className="loading-skeleton" style={{ width: '70px', height: '14px' }} />
          </div>
        </div>
      </motion.div>
    ))}
  </div>
);

const EmptyTransactions = ({ symbol }) => (
  <motion.div
    className="empty-state"
    initial={{ opacity: 0, scale: 0.98 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay: 0.2, duration: 0.3 }}
  >
    <RiHistoryLine size={28} />
    <p>No transactions found</p>
    <span>
      Transactions will appear here once you send or receive {symbol}
    </span>
  </motion.div>
);

const TransactionsList = ({ transactions }) => (
  <div className="transactions-list">
    {transactions.map((tx, index) => (
      <motion.div
        key={tx.hash}
        className={`transaction-item ${tx.type}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05, duration: 0.2 }}
        whileHover={{ x: 3 }}
      >
        <div className={`transaction-icon ${tx.type}`}>{tx.type === 'send' ? <FiSend /> : <FiArrowDownLeft />}</div>
        <div className="transaction-details">
          <div className="transaction-main">
            <span className="type">{tx.type === 'send' ? 'Sent' : tx.type === 'receive' ? 'Received' : 'Token'}</span>
            <span className={`amount ${tx.type}`}>{tx.formattedAmount} {tx.symbol}</span>
          </div>
          <div className="transaction-sub">
            <span><FiClock size={12} /> {tx.formattedDate} {tx.formattedTime}</span>
            <span>{tx.type === 'send' ? `To: ${tx.to}` : `From: ${tx.from}`}</span>
          </div>
        </div>
        <a href={tx.explorerUrl} target="_blank" rel="noopener noreferrer" className="view-button"><FiExternalLink /></a>
      </motion.div>
    ))}
  </div>
);

const TokenInformation = ({ tokenData }) => (
  <motion.div
    className="info-card"
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.1, duration: 0.3 }}
  >
    <h3>Token Information</h3>
    {[
      { label: 'Contract Address', value: tokenData?.testnetAddress || 'Native Token' },
      { label: 'Symbol', value: tokenData?.symbol },
      { label: 'Name', value: tokenData?.name },
      { label: 'Decimals', value: tokenData?.decimals },
      { label: 'Chain', value: tokenData?.chain || 'Unknown' },
      { label: 'Network', value: tokenData?.chain === 'BNB' ? 'BNB Chain Testnet' : 'TON Testnet' }
    ].map(({ label, value }) => (
      <div key={label} className="info-row">
        <span className="label">{label}</span>
        <span className="value">{value}</span>
      </div>
    ))}
  </motion.div>
);

const PriceStatistics = ({ priceData }) => (
  <motion.div
    className="info-card"
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.2, duration: 0.3 }}
  >
    <h3>Price Statistics</h3>
    {[
      { label: 'Current Price', value: `$${priceData.price.toFixed(2)}` },
      {
        label: '24h Change',
        value: `${priceData.change24h >= 0 ? '+' : ''}${priceData.change24h.toFixed(1)}%`,
        className: priceData.change24h >= 0 ? 'positive' : 'negative'
      },
      {
        label: '24h High',
        value: `$${(priceData.price * (1 + Math.abs(priceData.change24h) / 100)).toFixed(2)}`
      },
      {
        label: '24h Low',
        value: `$${(priceData.price * (1 - Math.abs(priceData.change24h) / 100)).toFixed(2)}`
      },
      {
        label: 'Last Updated',
        value: priceData.lastUpdated ? new Date(priceData.lastUpdated).toLocaleTimeString() : 'N/A'
      }
    ].map(({ label, value, className }) => (
      <div key={label} className="info-row">
        <span className="label">{label}</span>
        <span className={`value ${className || ''}`}>{value}</span>
      </div>
    ))}
  </motion.div>
);

const ExplorerButton = ({ address }) => (
  <motion.button
    className="explorer-button"
    onClick={() => window.open(`https://testnet.tonscan.org/address/${address}`, '_blank')}
    whileHover={{ scale: 1.02, y: -2 }}
    whileTap={{ scale: 0.98 }}
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.3, duration: 0.3 }}
  >
    <FiExternalLink />
    <span>View on Explorer</span>
  </motion.button>
);

export default TokenPage; 